import { MIME_HEADER_FIELD } from './candidateMimeHeaders';

const CRLF = '\r\n';
const BASE64_BODY = /^[A-Za-z0-9+/]+={0,2}$/;
const BASE64_QUANTUM = 4;
const REPLACED_HEADERS = new Set(['content-type', 'content-transfer-encoding']);

const unfoldHeaders = (headerBlock: string): string[] => headerBlock.split(/\r?\n/).reduce<string[]>(
    (fields, line) => {
        if (/^[ \t]/.test(line) && fields.length) {
            const previous = fields.pop() as string;
            return [...fields, `${previous}${CRLF}${line}`];
        }
        return [...fields, line];
    },
    [],
);

const parseField = (field: string): RegExpMatchArray | null => (
    field.replace(/\r?\n[ \t]+/g, ' ').match(MIME_HEADER_FIELD)
);

const headerName = (field: string): string => {
    const [, name = ''] = parseField(field) || [];
    return name.toLowerCase();
};

const headerValue = (field: string): string => {
    const [, , value = ''] = parseField(field) || [];
    return value;
};

const isBoundarylessMultipart = (fields: string[]): boolean => {
    const contentType = fields.find((field) => headerName(field) === 'content-type');
    if (!contentType) return false;
    const value = headerValue(contentType);
    return /^multipart\//i.test(value.trim()) && !/(?:^|;)\s*boundary\s*=/i.test(value);
};

const isUtf8Base64 = (body: string): boolean => {
    if (!body || body.length % BASE64_QUANTUM !== 0 || !BASE64_BODY.test(body)) return false;
    try {
        const decodedBytes = Uint8Array.from(atob(body), (character) => character.charCodeAt(0));
        new TextDecoder('utf-8', { fatal: true }).decode(decodedBytes);
        return true;
    } catch {
        return false;
    }
};

const recoverBoundarylessMultipart = (binary: string): string | undefined => {
    const separator = binary.match(/\r?\n\r?\n/);
    if (!separator || separator.index === undefined) return undefined;
    const fields = unfoldHeaders(binary.slice(0, separator.index));
    if (!isBoundarylessMultipart(fields)) return undefined;
    const body = binary.slice(separator.index + separator[0].length).replace(/\s+/g, '');
    if (!isUtf8Base64(body)) return undefined;
    return [
        ...fields.filter((field) => !REPLACED_HEADERS.has(headerName(field))),
        'Content-Type: text/plain; charset=utf-8',
        'Content-Transfer-Encoding: base64',
        '',
        body,
    ].join(CRLF);
};

export default recoverBoundarylessMultipart;
